import { action, computed, observable } from "mobx";
import PageRepository from "~/repository/PageRepository";
import NestStore from "~/stores/NestStore";
import Page from "../models/Page";

class PageStore extends NestStore {
  /**
   * @property {Array}
   */
  @observable pages = [];

  /**
   * @property {Object}
   */
  @observable page = new Page({});

  /**
   * @private
   */
  #pageCopy;

  /**
   * @property {number}
   */
  customerId;

  /**
   * @property boolean
   */
  activeOnly = false;

  /**
   * @property {number}
   */
  pageId;

  /**
   * @property boolean
   */
  @observable isSaved = false;

  constructor() {
    super();
    this.pageRepository = new PageRepository();
  }

  /**
   * Return if page has unsaved data or not
   * @returns {boolean}
   */
  @computed
  get hasUnsavedData() {
    return this.page.isDirty;
  }

  @action
  load() {
    this.isLoading = true;

    return Promise.all([this.getPages()]).then(() => {
      this.isLoading = false;
    });
  }

  @action
  loadOne() {
    this.isLoading = true;

    return Promise.all([this.getOne()]).then(() => {
      this.isLoading = false;
    });
  }

  @action
  reset() {
    this.errorMessages = {};
    this.errorMessage = "";
    this.page = new Page({});
  }

  @action
  getPages() {
    let query = null;

    if (this.customerId && this.activeOnly) {
      query = { customerId: this.customerId, isActive: 1 };
    } else if (this.customerId && !this.activeOnly) {
      query = { customerId: this.customerId };
    }

    return this.pageRepository.fetch(query).then(response => {
      let toursPageExists = false;

      this.pages = [];
      response.data.pages.forEach(pageArray => {
        const page = new Page(pageArray);

        // If it is this special page for My Tours
        // It needs to go before any other page
        if (page.isToursPage) {
          this.pages.unshift(page);
          toursPageExists = true;
        } else {
          this.pages.push(page);
        }
      });

      // If there is no special page, let's fake it
      if (!toursPageExists) {
        this.pages.unshift(new Page({ slug: "tours", isActive: true, isToursPage: true }));
      }
    });
  }

  @action
  getOne() {
    return this.pageRepository.fetchOne(this.pageId).then(response => {
      this.page = new Page(response.data.page);
    });
  }

  @action
  delete(pageId) {
    return this.pageRepository.delete(pageId);
  }

  @action
  create(data) {
    this.isLoading = true;
    return this.pageRepository
      .create(data)
      .then(response => {
        this.page = new Page(response.data.page);
        this.#pageCopy = response.data.page;
        this.isLoading = false;
        this.isSaved = true;
      })
      .catch(e => {
        this.isLoading = false;
        this.setErrorMessages(e);
        throw e;
      });
  }

  @action
  update(pageId, data) {
    this.isLoading = true;
    return this.pageRepository
      .update(pageId, data)
      .then(response => {
        this.page = new Page(response.data.page);
        this.#pageCopy = response.data.page;
        this.isLoading = false;
        this.isSaved = true;
      })
      .catch(e => {
        this.isLoading = false;
        this.setErrorMessages(e);
        throw e;
      });
  }
}

export default PageStore;
